import { useEffect, useMemo, useState } from "react";
import {
  FiDownload,
  FiDroplet,
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import ColorModal from "../../components/masters/ColorModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

export default function ColorsMasterPage() {
  const { accessToken } = useAuth();
  const [colors, setColors] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showModal, setShowModal] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadColors = async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest("/colors", { accessToken });

      setColors(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load colors.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadColors();
  }, [accessToken]);

  const filteredColors = useMemo(() => {
    const query = search.trim().toLowerCase();

    return colors.filter((color) => {
      const matchesSearch =
        !query ||
        color.name.toLowerCase().includes(query) ||
        color.code.toLowerCase().includes(query) ||
        color.hexCode?.toLowerCase().includes(query);

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && color.isActive) ||
        (statusFilter === "INACTIVE" && !color.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [colors, search, statusFilter]);

  const activeCount = colors.filter((color) => color.isActive).length;
  const colorsPagination = useClientPagination(filteredColors);

  const variantsUsingColors = colors.reduce(
    (total, color) =>
      total +
      (color._count?.productVariants || color.productVariants?.length || 0),
    0,
  );

  const handleSave = async (payload) => {
    if (selectedColor) {
      await apiRequest(`/colors/${selectedColor.id}`, {
        method: "PATCH",
        body: payload,
        accessToken,
      });
    } else {
      await apiRequest("/colors", {
        method: "POST",
        body: payload,
        accessToken,
      });
    }

    await loadColors();
    setSelectedColor(null);
  };

  const handleDelete = async (color) => {
    if (!window.confirm(`Delete "${color.name}"?`)) return;

    try {
      setError("");

      await apiRequest(`/colors/${color.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadColors();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete color.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Colors</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Colors Management
          </h1>

          <p className="mt-2 text-gray-500">
            Manage product colors used across inventory and variants.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 opacity-60"
          >
            <FiUpload />
            Import
          </button>

          <button
            type="button"
            disabled
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 opacity-60"
          >
            <FiDownload />
            Export
          </button>

          <button
            type="button"
            onClick={() => {
              setSelectedColor(null);
              setShowModal(true);
            }}
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white hover:bg-primary-800"
          >
            <FiPlus />
            Add Color
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Total Colors</p>
          <h2 className="mt-2 text-3xl font-bold">{colors.length}</h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Active</p>
          <h2 className="mt-2 text-3xl font-bold text-green-600">
            {activeCount}
          </h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Inactive</p>
          <h2 className="mt-2 text-3xl font-bold text-red-500">
            {colors.length - activeCount}
          </h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Variants Using Colors</p>
          <h2 className="mt-2 text-3xl font-bold">{variantsUsingColors}</h2>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-md">
            <FiSearch
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              placeholder="Search colors..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        {error && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Color
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Code
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Hex
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    Loading colors...
                  </td>
                </tr>
              ) : filteredColors.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    No colors found.
                  </td>
                </tr>
              ) : (
                colorsPagination.pageItems.map((color) => (
                  <tr
                    key={color.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div
                          className="h-12 w-12 rounded-2xl border border-gray-200"
                          style={{ backgroundColor: color.hexCode || "#FFFFFF" }}
                        />

                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {color.name}
                          </h4>

                          <p className="text-xs text-gray-500">
                            Product Color
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 font-medium">{color.code}</td>
                    <td className="px-6 py-4">{color.hexCode || "—"}</td>

                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          color.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {color.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedColor(color);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border hover:bg-primary-50 hover:text-primary-700"
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(color)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-red-500 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={colorsPagination} label="colours" />
      </div>

      <ColorModal
        open={showModal}
        color={selectedColor}
        onClose={() => {
          setShowModal(false);
          setSelectedColor(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}
